/** Public jobs portal: call-outs and roles at jobs.theoverhear.app */

export const JOBS_HOSTS = [
  'jobs.theoverhear.app',
  'jobs-theoverhear.web.app',
] as const;

export const JOB_APPLICATION_ID_PREFIX = 'jobapp_';
export const JOB_APPLICATION_ID_PATTERN = /^jobapp_[a-f0-9]{32}$/;

export function getHostname(): string {
  if (typeof window === 'undefined') {
    return '';
  }
  return window.location.hostname;
}

export function isJobsDomain(hostname = getHostname()): boolean {
  return (
    JOBS_HOSTS.includes(hostname as (typeof JOBS_HOSTS)[number]) ||
    hostname.includes('jobs')
  );
}

export function isJobsPublicPath(
  pathname: string,
  hostname = getHostname(),
): boolean {
  if (isJobsDomain(hostname)) {
    return true;
  }
  const path =
    pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return path === '/jobs' || path.startsWith('/jobs/');
}

export function getJobsPortalOrigin(hostname = getHostname()): string {
  const actualHost = getHostname();
  if (
    typeof window !== 'undefined' &&
    hostname === actualHost &&
    (isJobsDomain(actualHost) || actualHost === 'localhost' || actualHost === '127.0.0.1')
  ) {
    return window.location.origin;
  }
  if (isJobsDomain(hostname)) {
    return 'https://jobs.theoverhear.app';
  }
  return 'https://jobs.theoverhear.app';
}

export function getJobsListPath(hostname = getHostname()): string {
  return isJobsDomain(hostname) ? '/' : '/jobs';
}

export function getJobDetailPath(slug: string, hostname = getHostname()): string {
  const encoded = encodeURIComponent(slug);
  return isJobsDomain(hostname) ? `/${encoded}` : `/jobs/${encoded}`;
}

export function getJobApplyPath(
  slug: string,
  applicationId?: string,
  hostname = getHostname(),
): string {
  const base = `${getJobDetailPath(slug, hostname)}/apply`;
  if (!applicationId) {
    return base;
  }
  return `${base}?id=${encodeURIComponent(applicationId)}`;
}

export function buildJobPublicUrl(slug: string, hostname = getHostname()): string {
  return `${getJobsPortalOrigin(hostname)}${getJobDetailPath(slug, hostname)}`;
}

/** Always the jobs-host form, so shared links read jobs.theoverhear.app/<slug>/review. */
export function buildJobReviewUrl(slug: string, hostname = getHostname()): string {
  const origin = getJobsPortalOrigin(hostname);
  const onJobsHost = isJobsDomain(new URL(origin).hostname);
  const path = onJobsHost
    ? `/${encodeURIComponent(slug)}/review`
    : `/jobs/${encodeURIComponent(slug)}/review`;
  return `${origin}${path}`;
}

export function jobReviewSessionKey(slug: string): string {
  return `overhear.jobReview.${slug}`;
}

export function generateJobApplicationId(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${JOB_APPLICATION_ID_PREFIX}${hex}`;
}

export function isJobApplicationId(value: string): boolean {
  return JOB_APPLICATION_ID_PATTERN.test(value);
}

export function jobApplicationStorageKey(slug: string): string {
  return `overhear.jobApplication.${slug}`;
}
